/* import __COLOCATED_TEMPLATE__ from './discovery-module.hbs'; */
/* RESPONSIBLE TEAM: team-frontend-tech */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { assert, runInDebug } from '@ember/debug';
import { taskFor } from 'ember-concurrency-ts';
import { timeout } from 'ember-concurrency';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { action } from '@ember/object';
import type BannerDismissalService from 'embercom/services/banner-dismissal-service';

interface BaseArgs {
  heading: string;
  dismissalKey: string;
  enableMediaPlayIcon?: boolean;
  autoCollapse?: boolean;
  onDismiss?: () => void;
}

interface EditorialArgs extends BaseArgs {
  editorial: true;
  description?: never;
}

interface StandardArgs extends BaseArgs {
  editorial?: false;
  description: string;
}

type Args = EditorialArgs | StandardArgs;
interface Signature {
  Args: Args;
  Element: HTMLDivElement;
  Blocks: {
    actions: [];
    media: [];
  };
}

export default class DiscoveryModule extends Component<Signature> {
  @service declare intercomEventService: $TSFixMe;
  @service declare bannerDismissalService: BannerDismissalService;
  @tracked shouldShowBanner = false;
  @tracked isCollapsed = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    runInDebug(() => this.validateDismissConfig());
    taskFor(this.computeVisibility).perform();
    this.isCollapsed = this.args.autoCollapse || false;
  }

  private validateDismissConfig() {
    assert(
      `<PulseCandidate::DiscoveryModule>: @dismissalKey must be provided`,
      typeof this.args.dismissalKey === 'string',
    );
  }

  @restartableTask
  *computeVisibility() {
    let adminHasDismissedBanner: boolean = yield this.bannerDismissalService.hasDismissed(
      this.args.dismissalKey,
    );
    this.shouldShowBanner = !adminHasDismissedBanner;
    if (!adminHasDismissedBanner) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'viewed',
        object: 'discovery_banner',
        context: this.args.dismissalKey,
      });
    }
  }

  @action
  collapse(): void {
    if (!this.args.autoCollapse) {
      return;
    }
    taskFor(this.expand).cancelAll();
    this.isCollapsed = true;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'collapsed',
      object: 'discovery_banner',
      context: this.args.dismissalKey,
    });
  }

  @dropTask
  *expand() {
    if (!this.args.autoCollapse) {
      return;
    }
    yield timeout(500);
    this.isCollapsed = false;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'expanded',
      object: 'discovery_banner',
      context: this.args.dismissalKey,
    });
  }

  @restartableTask
  *handleDismissClick() {
    this.shouldShowBanner = false;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'dismissed',
      object: 'discovery_banner',
      context: this.args.dismissalKey,
    });
    yield this.bannerDismissalService.dismiss(this.args.dismissalKey);
    if (typeof this.args.onDismiss === 'function') {
      this.args.onDismiss();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'PulseCandidate::DiscoveryModule': typeof DiscoveryModule;
  }
}
